import { Link } from 'react-router-dom';
import { lazy, Suspense } from 'react';
import { Settings, Users } from 'lucide-react';
import {
  Avatar,
  Badge,
  Card,
  CardBody,
  CardHeader,
  DescriptionList,
  IconTile,
  PageContainer,
  PageHeader,
  Skeleton,
  buttonClass,
} from '@castinflow/ui';
import { useSession } from '../lib/session';
import { usePageMeta } from '../shell/page-meta';
import { ROLE_LABEL, tr } from '../i18n/tr';

/**
 * Genel bakış: kayıt okuma yetkisinde yönetim/ekip durumu (`framework/today/OverviewPage.tsx`; B2 — "Bugün"den ayrı sayfa);
 * bu yetkisi olmayan kullanıcıda hesap/kurulum özeti. Çerçeve tembel yüklenir.
 */
const WorkOverview = lazy(() => import('../framework/today/OverviewPage'));

export function HomePage() {
  usePageMeta(tr.home.title);
  const canRead = useSession((s) => s.me?.capabilities.includes('records.read') === true);
  const tenant = useSession((s) => s.me?.tenant);
  return canRead ? (
    <Suspense fallback={<Skeleton className="h-64 w-full" />}>
      <PageContainer>
        <div data-testid="home">
          {tenant ? (
            <p className="mb-2 break-words text-sm text-fg-muted" data-testid="home-tenant">
              {tenant.name} ({tenant.slug})
            </p>
          ) : null}
          <WorkOverview />
        </div>
      </PageContainer>
    </Suspense>
  ) : (
    <WorkspaceHome />
  );
}

function WorkspaceHome() {
  const me = useSession((s) => s.me);
  usePageMeta(tr.home.title);
  if (!me) return null;
  const canManageUsers = me.capabilities.includes('users.manage');
  const name = `${me.user.firstName} ${me.user.lastName}`.trim();
  return (
    <PageContainer>
      <PageHeader title={tr.home.title} description={tr.home.description} />
      <div
        className="grid grid-cols-1 items-start gap-4 lg:grid-cols-[minmax(0,1fr)_20rem]"
        data-testid="home"
      >
        <Card as="section" aria-labelledby="home-workspace">
          <CardHeader
            titleId="home-workspace"
            title={tr.home.welcome(name)}
            icon={<Avatar name={name} decorative />}
          />
          <CardBody>
            <DescriptionList
              items={[
                { term: tr.home.workspace, value: me.tenant.name },
                {
                  term: tr.home.address,
                  value: (
                    <span data-testid="home-tenant">
                      {me.tenant.name} (<code className="font-mono text-sm">{me.tenant.slug}</code>)
                    </span>
                  ),
                },
                {
                  term: tr.home.role,
                  value: <span data-testid="home-role">{ROLE_LABEL[me.user.role]}</span>,
                },
                {
                  term: tr.home.twoFactor,
                  value: me.user.totpEnabled ? (
                    <Badge tone="success">{tr.home.twoFactorOn}</Badge>
                  ) : (
                    <Badge>{tr.home.twoFactorOff}</Badge>
                  ),
                },
              ]}
            />
          </CardBody>
        </Card>
        <Card as="section" aria-labelledby="home-next">
          <CardHeader titleId="home-next" title={tr.home.nextTitle} />
          <ul className="divide-y divide-border">
            {canManageUsers ? (
              <li className="flex items-start gap-3 px-4 py-4 sm:px-5">
                <IconTile icon={<Users size={18} />} size="sm" />
                <div className="min-w-0 space-y-2">
                  <p className="text-base text-fg-muted">{tr.home.nextUsers}</p>
                  <Link to="/org" className={buttonClass('secondary', 'sm')}>
                    {tr.home.goUsers}
                  </Link>
                </div>
              </li>
            ) : null}
            <li className="flex items-start gap-3 px-4 py-4 sm:px-5">
              <IconTile icon={<Settings size={18} />} size="sm" tone="info" />
              <div className="min-w-0 space-y-2">
                <p className="text-base text-fg-muted">{tr.home.nextSettings}</p>
                <Link to="/settings" className={buttonClass('secondary', 'sm')}>
                  {tr.home.goSettings}
                </Link>
              </div>
            </li>
          </ul>
        </Card>
      </div>
    </PageContainer>
  );
}
